import React from "react";
import { AssetType } from "../store/slices/mapsSlice";
import { Link } from "react-router-dom";
import MyIcon from "../Common/MyIcon";
import CogIcon from "../../assets/icons/cog.svg2";
import AssetIcon from "../../assets/icons/asset-01.svg2";
import LocationIcon from "../../assets/icons/mapping.svg2";
import AssetsIcon from "./AssetsIcon";
import AssetKind from "./AssetKind";

export default function AssetList({
  showTable = false,
  assets,
  actions,
  selectable = false,
  selectedAssetUuids = [],
  onToggleAssetSelected,
  onTogglePageSelected,
  onAssetClick,
}: {
  showTable?: boolean;
  assets: AssetType[];
  actions?: any;
  selectable?: boolean;
  selectedAssetUuids?: string[];
  onToggleAssetSelected?: (uuid: string, selected: boolean) => void;
  onTogglePageSelected?: (selected: boolean) => void;
  onAssetClick?: (asset: AssetType) => void;
}) {
  const getActiveEventsCount = (asset: AssetType) =>
    asset.Stats?.ActiveEvents || 0;
  const selectableAssets = assets.filter((asset) => !!asset.Uuid);
  const selectedOnPage = selectableAssets.filter((asset) =>
    selectedAssetUuids.includes(asset.Uuid || "")
  );
  const allPageSelected =
    selectableAssets.length > 0 &&
    selectedOnPage.length === selectableAssets.length;

  const renderAssetLink = (asset: AssetType) => {
    if (onAssetClick) {
      return (
        <button
          type="button"
          className="btn btn-link p-0 border-0 align-baseline"
          onClick={() => onAssetClick(asset)}
        >
          {asset.Label}
        </button>
      );
    }
    return <Link to={`/assets/assets/${asset.Uuid}`}>{asset.Label}</Link>;
  };

  return (
    <>
      {showTable ? (
        <div className="table-responsive">
          <table className="table table-bg-transparent">
            <thead>
              <tr>
                {(selectable || actions) && (
                  <th>
                    {selectable && (
                      <input
                        className="form-check-input"
                        type="checkbox"
                        aria-label="Select all assets on this page"
                        checked={allPageSelected}
                        onChange={(e) =>
                          onTogglePageSelected?.(e.target.checked)
                        }
                      />
                    )}
                  </th>
                )}
                <th>Label</th>
                <th>Type</th>
                <th>Has Location</th>
                <th>Locations</th>
                <th>Contacts</th>
                <th>Groups</th>
                <th>Active events</th>
                <th>Dependant assets</th>
                <th>Connected assets</th>
              </tr>
            </thead>
            <tbody>
              {assets?.map((asset) => (
                <tr key={asset.Uuid}>
                  {(selectable || actions) && (
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        {selectable && asset.Uuid && (
                          <input
                            className="form-check-input"
                            type="checkbox"
                            aria-label={`Select ${asset.Label || "asset"}`}
                            checked={selectedAssetUuids.includes(asset.Uuid)}
                            onChange={(e) =>
                              onToggleAssetSelected?.(
                                asset.Uuid || "",
                                e.target.checked
                              )
                            }
                          />
                        )}
                        {actions && actions(asset)}
                      </div>
                    </td>
                  )}
                  <td>{renderAssetLink(asset)}</td>
                  <td>
                    <span className="text-nowrap">
                      <AssetsIcon assetKind={asset.Kind} />{" "}
                      <AssetKind assetKind={asset.Kind} />
                    </span>
                  </td>
                  <td>
                    {asset.HasLocation ? (
                      <span className="badge bg-success">YES</span>
                    ) : (
                      <span className="badge bg-secondary">NO</span>
                    )}
                  </td>
                  <td>
                    {asset.HasLocation ? asset.Stats?.Locations || 0 : "N/A"}
                  </td>
                  <td>{asset.Stats?.Contacts || 0}</td>
                  <td>{asset.Stats?.Groups || 0}</td>
                  <td>
                    {getActiveEventsCount(asset) > 0 ? (
                      <span className="badge bg-warning text-dark">
                        {getActiveEventsCount(asset)}
                      </span>
                    ) : (
                      0
                    )}
                  </td>
                  <td>{asset.Stats?.Children || 0}</td>
                  <td>{asset.Stats?.Parents || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="flex-grow-1">
          <div className="d-flex m-0 gap-2 flex-wrap">
            {assets.map((asset) => (
              <div
                key={asset.Uuid}
                className="card-light-gradient rounded-4 text-navy p-4 position-relative d-flex flex-column"
              >
                <div className="d-flex align-items-start">
                  <div>
                    <h3 className="fw-bold m-0">
                      {asset.Label || asset.Uuid?.slice(0, 5)}
                    </h3>
                    <div className=" text-secondary">
                      {asset.Reference ? `Ref: ${asset.Reference}` : <br />}
                    </div>
                    <div className=" text-secondary">
                      {asset.Description ? (
                        `Description: ${asset.Description}`
                      ) : (
                        <></>
                      )}
                    </div>
                    <div className=" text-secondary">
                      {asset.Stats?.Parents && asset.ParentAssets?.[0] ? (
                        <>
                          Parent:
                          <AssetsIcon
                            assetKind={asset.ParentAssets?.[0].Kind}
                          />{" "}
                          {asset.ParentAssets?.[0].Label} $
                          {asset.Stats?.Parents > 1
                            ? `(+${asset.Stats?.Parents - 1} more)`
                            : ""}
                        </>
                      ) : (
                        <br />
                      )}
                    </div>
                  </div>
                  <div className="flex-grow-1"></div>{" "}
                  {actions && (
                    <div className="d-flex align-items-center">
                      {actions(asset)}
                    </div>
                  )}
                  <div className="p-1 bg-purple">
                    <Link to={`/assets/assets/${asset.Uuid}`}>
                      {" "}
                      <MyIcon Icon={CogIcon} width={20} className="rounded-3" />
                    </Link>
                  </div>
                </div>
                <hr className="text-secondary" />
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <AssetsIcon assetKind={asset.Kind} width={30} />
                    <span className="text-black fw-bold me-2">Type:</span>
                    <span className="text-black">
                      <AssetKind assetKind={asset.Kind} />
                    </span>
                  </div>
                  <div className="d-flex align-items-center">
                    <MyIcon
                      Icon={LocationIcon}
                      width={25}
                      className="rounded-3 me-2"
                    />
                    <span className="text-black fw-bold me-2">Location:</span>{" "}
                    {asset.HasLocation ? (
                      <span className="badge bg-success">
                        {asset.Stats?.Locations || 0}
                      </span>
                    ) : (
                      <span className="badge bg-secondary">NO</span>
                    )}
                  </div>
                </div>
                <div className="my-2 small text-secondary">
                  Last updated at:{asset.UpdatedAt}
                </div>
                <div className="flex-grow-1"></div>
                <div className="bg-white py-2 rounded-3 d-flex justify-content-evenly">
                  <div className=" text-center border-end px-3">
                    <h5 className="fw-bold text-navy m-0">
                      {asset.Stats?.Contacts || 0}
                    </h5>
                    <div
                      className="fw-bold text-black"
                      style={{ fontSize: "0.6rem" }}
                    >
                      CONTACTS
                    </div>
                  </div>
                  <div className="text-center border-end px-3">
                    <h5 className="fw-bold text-navy m-0">
                      {asset.Stats?.Groups || 0}
                    </h5>
                    <div
                      className="fw-bold text-black"
                      style={{ fontSize: "0.6rem" }}
                    >
                      GROUPS
                    </div>
                  </div>
                  <div className=" text-center border-end px-3">
                    <h5 className="fw-bold text-navy m-0">
                      {getActiveEventsCount(asset)}
                    </h5>
                    <div
                      className="fw-bold text-black"
                      style={{ fontSize: "0.6rem", lineHeight: "0.7rem" }}
                    >
                      ACTIVE EVENTS
                    </div>
                  </div>
                  <div className=" text-center border-end px-3">
                    <h5 className="fw-bold text-navy m-0">
                      {asset.Stats?.Children || 0}{" "}
                    </h5>
                    <div
                      className="fw-bold text-black"
                      style={{ fontSize: "0.6rem", lineHeight: "0.7rem" }}
                    >
                      DEPENDANT ASSETS{" "}
                    </div>
                  </div>
                  <div className=" text-center border-end px-3">
                    <h5 className="fw-bold text-navy m-0">
                      {asset.Stats?.Parents || 0}
                    </h5>
                    <div
                      className="fw-bold text-black "
                      style={{ fontSize: "0.6rem", lineHeight: "0.7rem" }}
                    >
                      CONNECTED ASSETS
                    </div>
                  </div>
                </div>{" "}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
